import type { TenantRole } from '../types';

/**
 * What someone may do inside one tenant.
 *
 * This catalog is the contract between the API and the UI: the backend refuses on
 * it, and the frontend hides controls on the same strings, so a button that is
 * hidden is exactly a button that would have 403'd. Adding a capability means
 * adding it here first.
 *
 * The names are `<noun>.<verb>[.<qualifier>]` and the qualifier always *narrows*
 * — `post.publish.direct` is a strictly stronger thing than `post.publish`, not a
 * variant of it. That matters because the two-tier pairs below are how the review
 * workflow is expressed as authorization rather than as UI state.
 *
 * Deliberately NOT stored as a Postgres enum. Enum labels are append-only
 * forever, and this list changes with every feature.
 */
export const PERMISSIONS = [
  /** Rename the tenant, change its defaults. */
  'tenant.settings',
  /** Upgrade or downgrade the tenant's plan. Never held by a member. */
  'tenant.subscription.manage',

  'member.invite',
  'member.remove',
  /** Promote a member to admin or demote one. Bounded by MAX_TENANT_ADMINS. */
  'member.role.manage',
  /** Grant or revoke another member's permissions. */
  'member.permission.manage',

  'team.manage',
  /** See every team's work. The blanket form of a team_visibility_grants row. */
  'team.view.all',

  'post.create',
  /** Be assigned as a reviewer, and pass or fail a post under review. */
  'post.review',
  /**
   * Final approval. Admin-only by default and the reason `post.review` exists
   * separately: a peer review is a check, an approval is the gate.
   */
  'post.approve',
  /** Push an already-approved post live. */
  'post.publish',
  /** Publish or schedule without review at all. */
  'post.publish.direct',
  'post.schedule',
  /** Assign someone to keep track of a scheduled post. */
  'post.schedule.assign',

  'media.upload',
  /** Be assigned as a reviewer of an uploaded asset. */
  'media.review',
  /** Assign reviewers, share an asset across teams, delete someone else's. */
  'media.manage',

  /** Analytics for what this person created or reviewed. */
  'analytics.view.own',
  /** Analytics across the whole tenant. */
  'analytics.view.tenant',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET: ReadonlySet<string> = new Set<string>(PERMISSIONS);

/** Narrows an arbitrary string — a row read back from the database, say. */
export function isPermission(value: string): value is Permission {
  return PERMISSION_SET.has(value);
}

/**
 * What each role can do before any per-member override.
 *
 * An admin holds the whole catalog: there is no capability inside a tenant that
 * an admin is denied, and inventing one would only mean the person who can grant
 * themselves permissions has to take a detour to do it.
 *
 * A member's defaults are the shape the product describes: they create, they
 * review, they schedule, they upload — and everything they produce still passes
 * an admin. Note what is absent. `post.approve` is absent, so a member's approval
 * is a peer review and never the gate. `post.publish.direct` is absent, so a
 * member can push an *approved* post live but can never skip the review.
 */
export const ROLE_DEFAULT_PERMISSIONS: Record<TenantRole, readonly Permission[]> = {
  admin: PERMISSIONS,
  member: [
    'post.create',
    'post.review',
    'post.publish',
    'post.schedule',
    'media.upload',
    'media.review',
    'analytics.view.own',
  ],
};

/**
 * A tenant may have at most this many admins.
 *
 * Mirrored by the `enforce_tenant_admin_cap` trigger in
 * supabase-migration-tenancy.sql. Change both together — the trigger is what
 * holds the line when two admins promote someone at the same moment, since the
 * service's count-then-insert cannot.
 */
export const MAX_TENANT_ADMINS = 3;

export type PermissionEffect = 'grant' | 'revoke';

/** One admin's deliberate deviation from a member's role defaults. */
export interface PermissionOverride {
  permission: Permission;
  effect: PermissionEffect;
}

/**
 * Role defaults ∪ grants − revokes, in that order.
 *
 * Revoke wins over grant for the same permission, which cannot arise from the
 * table (the primary key is one row per permission) but can from a hand-built
 * array — and a permission system that resolves an ambiguity by granting is the
 * wrong way round.
 *
 * Returned in catalog order rather than insertion order so two equal sets always
 * serialise identically; the frontend caches these and compares them.
 */
export function resolveEffectivePermissions(
  role: TenantRole,
  overrides: readonly PermissionOverride[] = []
): Permission[] {
  const effective = new Set<Permission>(ROLE_DEFAULT_PERMISSIONS[role]);

  for (const { permission, effect } of overrides) {
    if (effect === 'grant') effective.add(permission);
  }
  for (const { permission, effect } of overrides) {
    if (effect === 'revoke') effective.delete(permission);
  }

  return PERMISSIONS.filter((permission) => effective.has(permission));
}

export function hasPermission(
  granted: readonly Permission[],
  permission: Permission
): boolean {
  return granted.includes(permission);
}

/**
 * Whether work authored by someone in this role still needs an admin to approve
 * it, however many peers have already signed off.
 *
 * This is a workflow rule and not a permission, which is why it lives as a
 * function rather than as an absent capability: a member genuinely may review and
 * may publish, and what is being expressed is that neither of those closes the
 * loop on their own post. Keeping it here means the composer, the approvals queue
 * and the publish worker cannot each decide it differently.
 */
export function requiresAdminApproval(authorRole: TenantRole): boolean {
  return authorRole === 'member';
}
